import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, AlertCircle, CheckCircle, XCircle, Clock, Lock,
  Activity, TrendingUp, Layers, AlertTriangle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import config from '@/config';

// Colour roles.
//
// Magnitude charts use ONE validated hue rather than a colour per stage: with
// 23 stages a categorical palette would have to cycle, and cycled hues stop
// meaning anything. Status colours appear only in tiles, always with an icon,
// a label and the number - completed-green against failed-red measures dE 4.1
// under deuteranopia, so colour alone would be unreadable for those viewers.
const SERIES = '#2a78d6';        // validated: light L-band, chroma, contrast all pass
const STATUS = {
  completed: { color: '#0ca30c', Icon: CheckCircle, label: 'Completed' },
  failed:    { color: '#d03b3b', Icon: XCircle,     label: 'Failed' },
  pending:   { color: '#6b7280', Icon: Clock,       label: 'Pending' },
  running:   { color: '#fab219', Icon: Activity,    label: 'Running' },
  frozen:    { color: '#6b7280', Icon: Layers,      label: 'Frozen' },
};

const fmt = (n) => (n == null ? '—' : n.toLocaleString());

const fmtDuration = (minutes) => {
  if (minutes == null) return '—';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
};

function StatTile({ icon: Icon, label, value, sub, accent, emphasis }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" style={{ color: accent || '#6b7280' }} />
        <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">{label}</span>
      </div>
      <div className={`mt-2 font-semibold text-gray-900 ${emphasis ? 'text-3xl' : 'text-2xl'}`}>
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

/**
 * Horizontal magnitude bars: one row per stage, one hue, sorted.
 * Values are direct-labelled so the chart is readable without the axis, and
 * without colour carrying any of the meaning.
 */
function StageBars({ rows, valueKey, formatValue, emptyLabel, max }) {
  if (!rows.length) {
    return <div className="text-sm text-gray-500 py-8 text-center">{emptyLabel}</div>;
  }
  const peak = max ?? Math.max(...rows.map((r) => r[valueKey] || 0), 1);

  return (
    <div className="space-y-2">
      {rows.map((row) => {
        const value = row[valueKey] || 0;
        const pct = peak > 0 ? (value / peak) * 100 : 0;
        return (
          <div key={row.item_id} className="group">
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <span className="text-xs text-gray-700 truncate" title={row.name}>{row.name}</span>
              <span className="text-xs font-semibold text-gray-900 tabular-nums flex-shrink-0">
                {formatValue ? formatValue(value, row) : fmt(value)}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all group-hover:opacity-80"
                style={{ width: `${Math.max(pct, value > 0 ? 1.5 : 0)}%`, backgroundColor: SERIES }}
                title={`${row.name}: ${formatValue ? formatValue(value, row) : fmt(value)}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Daily throughput. Buckets are contiguous and include days with no activity,
 * so the horizontal axis is actually time.
 */
function ThroughputChart({ data }) {
  const [hover, setHover] = useState(null);
  if (!data?.length) return <div className="text-sm text-gray-500 py-8 text-center">No data</div>;

  const peak = Math.max(...data.map((d) => d.completed + d.failed), 1);
  const active = data.filter((d) => d.completed || d.failed).length;

  return (
    <div>
      <div className="relative h-40 flex items-end gap-[2px]">
        {data.map((d, i) => {
          const total = d.completed + d.failed;
          const pct = (total / peak) * 100;
          return (
            <div
              key={d.date}
              className="flex-1 h-full flex items-end relative"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <div
                className="w-full rounded-t transition-all"
                style={{
                  height: `${Math.max(pct, total > 0 ? 1.5 : 0)}%`,
                  backgroundColor: SERIES,
                  opacity: hover === null || hover === i ? 1 : 0.45,
                }}
              />
              {hover === i && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap
                                bg-gray-900 text-white text-xs rounded px-2 py-1 shadow-lg">
                  <div className="font-medium">{d.date}</div>
                  <div>{fmt(d.completed)} completed</div>
                  {d.failed > 0 && <div className="text-red-300">{fmt(d.failed)} failed</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>{data[0]?.date}</span>
        <span>{active} of {data.length} days with activity · peak {fmt(peak)}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function WorkflowAnalyticsDashboard() {
  const { authenticatedFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/nodes/workflow-nodes/analytics/summary?days=${days}`
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${response.status}`);
      }
      setData(await response.json());
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, days]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const totals = data?.totals;
  const stages = data?.stages || [];
  const byStatus = totals?.by_status || {};

  const failing = stages
    .filter((s) => s.failed > 0)
    .sort((a, b) => (b.failure_rate || 0) - (a.failure_rate || 0));
  const backlogged = stages
    .filter((s) => s.backlog > 0)
    .sort((a, b) => b.backlog - a.backlog);

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflow Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">
            Pipeline health across every stage
            {data?.generated_in_ms != null && (
              <span className="text-gray-400"> · computed in {data.generated_in_ms} ms</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">{error}</div>
        </div>
      )}

      {loading && !data ? (
        <div className="py-20 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin" />
          <span className="text-sm">Loading analytics…</span>
        </div>
      ) : !totals ? null : (
        <>
          {/* Headline numbers. Every tile carries an icon and a label, so status
              is never conveyed by colour alone. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatTile
              icon={Layers} label="Total nodes" value={fmt(totals.nodes)}
              sub={`${stages.length} stages`} emphasis
            />
            <StatTile
              icon={CheckCircle} label="Success rate"
              value={totals.success_rate != null ? `${totals.success_rate}%` : '—'}
              sub={`${fmt(byStatus.completed)} completed`}
              accent={STATUS.completed.color} emphasis
            />
            <StatTile
              icon={XCircle} label="Failed" value={fmt(byStatus.failed)}
              sub={failing.length ? `across ${failing.length} stages` : 'none'}
              accent={STATUS.failed.color}
            />
            <StatTile
              icon={Clock} label="Pending" value={fmt(byStatus.pending)}
              sub="waiting to run" accent={STATUS.pending.color}
            />
            <StatTile
              icon={Activity} label="Running" value={fmt(byStatus.running)}
              sub={`${fmt(totals.currently_locked)} locked`} accent={STATUS.running.color}
            />
            <StatTile
              icon={TrendingUp} label="Median latency"
              value={fmtDuration(data.latency?.median_minutes)}
              sub={`p90 ${fmtDuration(data.latency?.p90_minutes)}`}
            />
          </div>

          {/* Throughput */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-900">Nodes completed per day</h2>
              <span className="text-xs text-gray-500">last {days} days</span>
            </div>
            <ThroughputChart data={data.throughput} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Where the pipeline actually hurts */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="text-sm font-semibold text-gray-900">Failure rate by stage</h2>
                <span className="text-xs text-gray-500">of finished nodes</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Stages with at least one failure, worst first.
              </p>
              <StageBars
                rows={failing}
                valueKey="failure_rate"
                max={Math.max(...failing.map((s) => s.failure_rate || 0), 1)}
                formatValue={(v, row) => `${v}%  (${fmt(row.failed)})`}
                emptyLabel="No failures recorded."
              />
            </div>

            {/* What is queued up */}
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-baseline justify-between mb-1">
                <h2 className="text-sm font-semibold text-gray-900">Backlog by stage</h2>
                <span className="text-xs text-gray-500">pending + running</span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Work waiting at each stage, largest first.
              </p>
              <StageBars rows={backlogged} valueKey="backlog" emptyLabel="Nothing queued." />
            </div>
          </div>

          {/* The full table - the accessible view of everything above */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">All stages</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-2 font-medium">Stage</th>
                    <th className="text-right px-3 py-2 font-medium">Total</th>
                    {Object.keys(STATUS).map((s) => (
                      <th key={s} className="text-right px-3 py-2 font-medium">{STATUS[s].label}</th>
                    ))}
                    <th className="text-right px-5 py-2 font-medium">Failure rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stages.map((s) => (
                    <tr key={s.item_id} className="hover:bg-gray-50">
                      <td className="px-5 py-2 text-gray-900">{s.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">{fmt(s.total)}</td>
                      {Object.keys(STATUS).map((key) => (
                        <td key={key} className="px-3 py-2 text-right tabular-nums text-gray-600">
                          {s[key] ? fmt(s[key]) : <span className="text-gray-300">0</span>}
                        </td>
                      ))}
                      <td className="px-5 py-2 text-right tabular-nums">
                        {s.failure_rate == null ? (
                          <span className="text-gray-300">—</span>
                        ) : s.failure_rate > 0 ? (
                          <span className="inline-flex items-center gap-1 font-medium text-gray-900">
                            <AlertTriangle className="w-3 h-3" style={{ color: STATUS.failed.color }} />
                            {s.failure_rate}%
                          </span>
                        ) : (
                          <span className="text-gray-500">0%</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data.latency?.measures && (
            <p className="mt-3 text-xs text-gray-500">
              Latency is the {data.latency.measures}, median over{' '}
              {fmt(data.latency.sample_size)} nodes completed in the last{' '}
              {data.latency.window_days} days. A re-run resets a node's clock.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default WorkflowAnalyticsDashboard;
